import { useEffect, useState } from "react";
import { addCartItem, fetchCart, subscribeToCartUpdates, type CartResponse } from "../lib/cart";

function toCartQuantityMap(cart: CartResponse | null) {
  const counts = new Map<number, number>();

  if (!cart) {
    return counts;
  }

  for (const item of cart.items) {
    counts.set(item.productId, item.quantity);
  }

  return counts;
}

export function useCart() {
  const [cart, setCart] = useState<CartResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let ignore = false;

    async function loadCart() {
      setLoading(true);

      try {
        const result = await fetchCart();
        if (!ignore) {
          setCart(result);
          setError(null);
        }
      } catch {
        if (!ignore) {
          setError("Unable to load cart.");
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    void loadCart();

    const unsubscribe = subscribeToCartUpdates((nextCart) => {
      if (!ignore) {
        setCart(nextCart);
      }
    });

    return () => {
      ignore = true;
      unsubscribe();
    };
  }, []);

  async function addOne(productId: number) {
    const nextCart = await addCartItem({
      productId,
      quantity: 1
    });

    setCart(nextCart);
    return nextCart;
  }

  return {
    cart,
    cartCounts: toCartQuantityMap(cart),
    loading,
    error,
    addOne
  };
}
